import {
  type LlmRoutePurposeAny,
  modelDisplayName,
  modelPickerOptions,
  type RouteChange,
} from '@developerz/domain';
import { IconChevronDown, IconLock } from '@developerz/ui';
import { createEffect, createMemo, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { routePinDisplay } from '../lib/agents';
import type { KeyRow } from '../lib/keys';
import type { RouteWire } from '../lib/llmRoutes';
import { changeFor, type SelectableRow } from '../lib/model-picker';
import { createNarrowViewport } from '../lib/viewport';
import { ActionDrawer } from './ActionDrawer';
import { providerLabel } from './KeyCard';
import { type KeysState, ModelPickerList } from './ModelPickerList';
import { Popover } from './Popover';

// One purpose's model picker (#5210, epic #5200), on an agent card and the Default
// card. The trigger shows the pin or "Automatic"; opening it lists what
// `modelPickerOptions` (`@developerz/domain`) offers over the ORG keys list — the
// resolver's own lane fence and parked predicates, never re-derived here. A pick is
// reported as a `RouteChange`; the caller's save (`lib/agent-model-save.ts`) turns it
// into the full-row write.
//
// Desktop: the anchored `Popover` (#5205). Phone (narrower than `sm`): the bottom
// sheet, `ActionDrawer side="bottom"`. Either way focus returns to the trigger on
// every close path — the Popover's cleanup, the sheet's dialog trap.
//
// LOCKED (no `write:billing`, or the API answered 403): a disabled trigger with a
// lock and "Ask an admin". The server is still the arbiter (`apps/dashboard/CLAUDE.md`
// § Boundary) — this only hides a control that would plainly 403.
//
// BUSY (a save for this purpose is in flight): the trigger is disabled, `aria-busy`
// and reads "Saving…", and a pick in a panel that was already open is ignored — the
// save lock DROPS a second call rather than queue it (`lib/keyed-mutation.ts`), and
// Coder and DevOps share the `general` lock, so the sibling card's open panel is
// reachable mid-save. Disabling the trigger a pick just handed focus back to drops
// that focus in a browser; `createSettleRefocus` hands it back when the save settles.

export type ModelPickerProps = {
  /** The agent's display name, for the trigger's accessible name and the sheet title. */
  readonly agentName: string;
  readonly purpose: LlmRoutePurposeAny;
  /** The purpose's ORG route, or undefined when it has none. */
  readonly route: RouteWire | undefined;
  /** The trigger's static value (`pinTriggerValue`), null for "Automatic". */
  readonly pinValue: string | null;
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onChoose: (change: RouteChange) => void;
  /** The trigger's unpinned copy — defaults to `t('agents.automatic')`. The
   * Coder's task-tag rows (#5211) pass `t('agents.drawer.task_tags.unpinned')`
   * ("Uses Default model") instead: "Automatic" reads as pool-wide resolution,
   * but an unpinned task tag defers to the Default card's own pin specifically. */
  readonly automaticLabel?: string;
  /** The model the resolver would pick right now (the card's preview `pick.model`).
   * With no pin and no `automaticLabel` the trigger reads "Automatic → <name>". */
  readonly previewPick?: string | null | undefined;
};

export type PinFace = { readonly primary: string; readonly sub: string | null };

/**
 * A pin in words, the one fold behind the picker's trigger face AND the compact
 * per-repository row summary (#5626): a model by its display name with the provider
 * quieter beside it, a key by its label with the model beside it, Automatic with what
 * it resolves to. Pure over the route and its `pinTriggerValue`.
 */
export function pinFace(
  route: RouteWire | undefined,
  pinValue: string | null,
  opts: {
    readonly automaticLabel?: string | undefined;
    readonly previewPick?: string | null | undefined;
  } = {},
): PinFace {
  if (pinValue === null) {
    const pick = opts.previewPick;
    if (opts.automaticLabel === undefined && pick !== null && pick !== undefined) {
      return {
        primary: t('agents.picker.automatic_with', { model: modelDisplayName(pick) }),
        sub: null,
      };
    }
    return { primary: opts.automaticLabel ?? t('agents.automatic'), sub: null };
  }
  const pin = routePinDisplay(route);
  if (pin === null) return { primary: pinValue, sub: null };
  if (pin.keyLabel !== null) {
    return {
      primary: pin.keyLabel,
      sub: pin.model === null ? null : modelDisplayName(pin.model),
    };
  }
  if (pin.model !== null) {
    return {
      primary: modelDisplayName(pin.model),
      sub: pin.provider === null ? null : providerLabel(pin.provider),
    };
  }
  return {
    primary: pin.provider === null ? pinValue : providerLabel(pin.provider),
    sub: null,
  };
}

/**
 * Focus for a trigger disabled by its own pick's save: `arm()` at the pick; when
 * `busy` next clears and focus is nowhere (the browser dropped it off the disabled
 * trigger), it goes back to the trigger. Focus the user moved elsewhere stays put.
 */
function createSettleRefocus(root: () => HTMLElement | undefined, busy: () => boolean): () => void {
  let armed = false;
  createEffect(
    on(busy, (now) => {
      if (now || !armed) return;
      armed = false;
      const active = document.activeElement;
      if (active !== null && active !== document.body) return;
      root()?.querySelector<HTMLButtonElement>('.model-picker__trigger')?.focus({
        preventScroll: true,
      });
    }),
  );
  return () => {
    armed = true;
  };
}

export function ModelPicker(props: ModelPickerProps): JSX.Element {
  let root: HTMLDivElement | undefined;
  const armRefocus = createSettleRefocus(
    () => root,
    () => props.busy,
  );
  const narrow = createNarrowViewport();
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const options = createMemo(() =>
    modelPickerOptions(props.keys.data ?? [], {
      purpose: props.purpose,
      currentPin: props.route ?? null,
    }),
  );
  const keysState = (): KeysState =>
    props.keys.data !== undefined ? 'ready' : props.keys.isError ? 'failed' : 'loading';
  const face$ = (): PinFace =>
    pinFace(props.route, props.pinValue, {
      automaticLabel: props.automaticLabel,
      previewPick: props.previewPick,
    });
  /**
   * The trigger's accessible name: what the trigger SHOWS, in the order it shows it
   * (the face, its sub line, the saving note while a pick saves), then the raw pin
   * in parentheses. The shown words come first for WCAG 2.5.3 (label in name),
   * enforced by axe-core's `label-content-name-mismatch` from 4.14, so a speech user
   * can say what they see; that rule drops parentheticals before it compares, so
   * the raw id stays in the name for screen readers without breaking it.
   */
  const shown = (...extra: (string | null)[]): string => {
    const face = face$();
    const text = [face.primary, ...extra]
      .filter((s): s is string => s !== null && s !== '')
      .join(', ');
    return props.pinValue === null || props.pinValue === face.primary
      ? text
      : `${text} (${props.pinValue})`;
  };
  const ariaLabel = (): string =>
    t('agents.picker.trigger_aria', {
      agent: props.agentName,
      value: shown(face$().sub, props.busy ? t('agents.picker.saving') : null),
    });

  const face = (): JSX.Element => (
    <>
      {/* The `{' '}` text nodes render as nothing (the trigger is a flex row, which
          drops whitespace-only runs) but keep the spans' words apart in the text an
          accessibility checker reads off the box: without them "Opus 4.8" and
          "Anthropic" read as one word, "8Anthropic", that the name does not contain. */}
      <span class="model-picker__value">{face$().primary}</span>{' '}
      <Show when={face$().sub}>{(sub) => <span class="model-picker__sub">{sub()}</span>}</Show>
      <Show when={props.busy}>
        {' '}
        <span class="model-picker__saving">{t('agents.picker.saving')}</span>
      </Show>
      <IconChevronDown class="model-picker__chevron" size="0.875rem" />
    </>
  );

  const list = (close: () => void): JSX.Element => (
    <ModelPickerList
      options={options()}
      keysState={keysState()}
      label={t('agents.picker.listbox')}
      onChoose={(row: SelectableRow) => {
        if (props.busy) return;
        close();
        armRefocus();
        props.onChoose(changeFor(row));
      }}
    />
  );

  return (
    <div class="model-picker" ref={root}>
      <Show
        when={props.canEdit}
        fallback={
          <button
            type="button"
            class="model-picker__trigger model-picker__trigger--locked"
            disabled
            aria-label={t('agents.picker.trigger_aria', {
              agent: props.agentName,
              value: shown(t('agents.picker.locked')),
            })}
          >
            <IconLock class="model-picker__lock" size="0.875rem" />
            <span class="model-picker__value">{face$().primary}</span>{' '}
            <span class="model-picker__sub">{t('agents.picker.locked')}</span>
          </button>
        }
      >
        <Show
          when={narrow()}
          fallback={
            <Popover
              label={ariaLabel()}
              trigger={face()}
              triggerClass="model-picker__trigger"
              panelClass="model-picker__popover"
              busy={props.busy}
            >
              {list}
            </Popover>
          }
        >
          <button
            type="button"
            class="model-picker__trigger"
            aria-haspopup="dialog"
            aria-label={ariaLabel()}
            disabled={props.busy}
            aria-busy={props.busy ? 'true' : undefined}
            onClick={() => setSheetOpen(true)}
          >
            {face()}
          </button>
          <ActionDrawer
            open={sheetOpen()}
            side="bottom"
            title={t('agents.picker.sheet_title', { agent: props.agentName })}
            onClose={() => setSheetOpen(false)}
          >
            <Show when={sheetOpen()}>{list(() => setSheetOpen(false))}</Show>
          </ActionDrawer>
        </Show>
      </Show>
    </div>
  );
}
